import { apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export type InsuranceClaimStatus =
  | 'pending_submission'
  | 'submitted'
  | 'pending_approval'
  | 'approved'
  | 'repair_authorized'
  | 'invoice_submitted'
  | 'payment_received'
  | 'denied'
  | 'cancelled';

export type InsuranceClaim = {
  id: string;
  tenantId: string;
  storeId: string;
  ticketId: string;
  customerId?: string | null;
  insuranceProvider: string;
  claimNumber?: string | null;
  authorizationNumber?: string | null;
  status: InsuranceClaimStatus;
  approvedAmount?: string | null;
  deductibleAmount: string;
  shopPayout?: string | null;
  notes?: string | null;
  denialReason?: string | null;
  authorizationDocUrl?: string | null;
  invoiceNumber?: string | null;
  claimSubmittedAt?: string | null;
  claimApprovedAt?: string | null;
  repairAuthorizedAt?: string | null;
  invoiceSubmittedAt?: string | null;
  paymentReceivedAt?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type InsuranceClaimListParams = {
  status?: InsuranceClaimStatus | string;
  ticketId?: string;
  page?: number;
  pageSize?: number;
};

export type CreateInsuranceClaimBody = {
  ticketId: string;
  insuranceProvider?: string;
  claimNumber?: string;
  authorizationNumber?: string;
  approvedAmount?: number;
  deductibleAmount?: number;
  shopPayout?: number;
  notes?: string;
  authorizationDocUrl?: string;
  invoiceNumber?: string;
};

export type UpdateInsuranceClaimBody = Partial<
  Omit<CreateInsuranceClaimBody, 'ticketId'> & {
    status: InsuranceClaimStatus;
    denialReason: string | null;
  }
>;

export function listInsuranceClaims(params?: InsuranceClaimListParams) {
  return apiGet<InsuranceClaim[], PaginatedMeta>('/api/v1/insurance/claims', { params });
}

export function getInsuranceClaim(id: string) {
  return apiGet<InsuranceClaim>(`/api/v1/insurance/claims/${id}`).then((r) => r.data);
}

export function createInsuranceClaim(body: CreateInsuranceClaimBody) {
  return apiPost<InsuranceClaim, CreateInsuranceClaimBody>('/api/v1/insurance/claims', body).then(
    (r) => r.data,
  );
}

export function updateInsuranceClaim(id: string, body: UpdateInsuranceClaimBody) {
  return apiPatch<InsuranceClaim, UpdateInsuranceClaimBody>(`/api/v1/insurance/claims/${id}`, body).then(
    (r) => r.data,
  );
}

export function advanceInsuranceClaim(id: string) {
  return apiPost<InsuranceClaim>(`/api/v1/insurance/claims/${id}/advance`).then((r) => r.data);
}

export const INSURANCE_STATUS_FLOW: InsuranceClaimStatus[] = [
  'pending_submission',
  'submitted',
  'pending_approval',
  'approved',
  'repair_authorized',
  'invoice_submitted',
  'payment_received',
];

export const INSURANCE_STATUS_LABELS: Record<InsuranceClaimStatus, string> = {
  pending_submission: 'Pending submission',
  submitted: 'Submitted',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  repair_authorized: 'Repair authorized',
  invoice_submitted: 'Invoice submitted',
  payment_received: 'Payment received',
  denied: 'Denied',
  cancelled: 'Cancelled',
};
